import React, { useState } from 'react';
import { X, Trash2, AlertTriangle, Loader2, RotateCcw, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const DELETION_REASON_CODES = [
  { 
    value: 'duplicate_entry', 
    label: 'Duplicate Entry',
    shortDesc: 'Duplicate already exists',
    icon: '📋'
  },
  { 
    value: 'obsolete_no_replacement', 
    label: 'Obsolete',
    shortDesc: 'No longer available',
    icon: '⏹️'
  },
  { 
    value: 'obsolete_with_replacement', 
    label: 'Replaced',
    shortDesc: 'Superseded by another part',
    icon: '🔄',
    requiresReplacement: true
  },
  { 
    value: 'data_entry_error', 
    label: 'Data Error',
    shortDesc: 'Incorrect information',
    icon: '❌'
  },
  { 
    value: 'consolidation', 
    label: 'Consolidation',
    shortDesc: 'Merged with another part',
    icon: '🔗'
  },
  { 
    value: 'other', 
    label: 'Other',
    shortDesc: 'Specify in comments',
    icon: '📝',
    requiresComment: true
  },
];

const DeletePartModal = ({ isOpen, onClose, part, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [comment, setComment] = useState('');
  const [replacementHpn, setReplacementHpn] = useState('');
  const [confirmText, setConfirmText] = useState('');
  
  const selectedReasonData = DELETION_REASON_CODES.find(r => r.value === selectedReason);
  const requiresComment = selectedReasonData?.requiresComment;
  const requiresReplacement = selectedReasonData?.requiresReplacement;
  
  const isValid = () => {
    if (!selectedReason) return false;
    if (requiresComment && !comment.trim()) return false;
    if (requiresReplacement && !replacementHpn.trim()) return false;
    if (confirmText !== part?.hpn_compact) return false;
    return true;
  };
  
  const handleClose = () => {
    setSelectedReason('');
    setComment('');
    setReplacementHpn('');
    setConfirmText('');
    onClose();
  };
  
  const handleDelete = async () => {
    if (!isValid()) return;
    
    setIsSubmitting(true);
    
    try {
      const deleteData = {
        reason_code: selectedReason,
        comment: comment || null,
        replacement_hpn: requiresReplacement ? replacementHpn : null,
      };
      
      await api.delete(`/api/v1/parts/by-hpn/${part.hpn_compact}`, { data: deleteData });
      
      toast.success(
        <div>
          <p className="font-semibold">Part archived successfully</p>
          <p className="text-sm text-slate-600 mt-1">
            {part.hpn_display} has been moved to archive
          </p>
        </div>
      );
      
      onSuccess?.();
      handleClose();
      
    } catch (error) {
      console.error('Failed to delete part:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete part';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen || !part) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal - Wider and more compact */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden animate-fade-in">
        {/* Fixed Header */}
        <div className="bg-red-600 text-white px-5 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Trash2 className="w-5 h-5" />
            <div>
              <h2 className="text-base font-semibold">Archive Part</h2>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="font-mono text-sm font-medium">{part.hpn_display}</p>
              <p className="text-xs text-red-200 truncate max-w-48">{part.description?.slice(0, 40)}...</p>
            </div>
            <button
              onClick={handleClose}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="close-delete-modal-button"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content - Compact */}
        <div className="p-5 space-y-4">
          {/* Warning - Compact */}
          <div className="flex items-center space-x-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              Part will be hidden from search but can be restored later. All data preserved.
            </p>
          </div>
          
          {/* Reason Selection - 2 Column Grid */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Reason for Deletion <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {DELETION_REASON_CODES.map((reason) => (
                <label
                  key={reason.value}
                  className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedReason === reason.value
                      ? 'border-red-400 bg-red-50 ring-1 ring-red-400'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="deletion_reason"
                    value={reason.value}
                    checked={selectedReason === reason.value}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="sr-only"
                    data-testid={`reason-${reason.value}`}
                  />
                  <span className="text-lg">{reason.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-900">{reason.label}</p>
                    <p className="text-xs text-slate-500 truncate">{reason.shortDesc}</p>
                  </div>
                  {selectedReason === reason.value && (
                    <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></div>
                  )}
                </label>
              ))}
            </div>
          </div>
          
          {/* Conditional Fields - Side by side when both present */}
          <div className={`grid gap-3 ${requiresReplacement ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {/* Replacement HPN */}
            {requiresReplacement && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <label className="block text-xs font-medium text-blue-800 mb-1.5 flex items-center">
                  <Link2 className="w-3 h-3 mr-1" />
                  Replacement HPN <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={replacementHpn}
                  onChange={(e) => setReplacementHpn(e.target.value.toUpperCase())}
                  placeholder="e.g., R1C112L"
                  className="w-full px-3 py-2 text-sm border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono uppercase"
                  data-testid="replacement-hpn-input"
                />
              </div>
            )}
            
            {/* Comment */}
            <div className={requiresReplacement ? '' : ''}>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Comment {requiresComment && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={requiresComment ? 'Required: explain reason...' : 'Optional notes'}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                data-testid="delete-comment"
              />
            </div>
          </div>
          
          {/* Confirmation - Compact */}
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <label className="block text-xs font-medium text-red-800 mb-1.5">
              Type <span className="font-mono font-bold">{part.hpn_compact}</span> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              placeholder="Enter HPN to confirm"
              className="w-full px-3 py-2 text-sm border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent font-mono uppercase"
              data-testid="delete-confirm-input"
            />
          </div>
        </div>
        
        {/* Fixed Footer */}
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <div className="flex items-center space-x-2 text-xs text-slate-500">
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Can be restored later</span>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900"
              data-testid="delete-cancel-button"
            >
              Cancel
            </button>
            
            <button
              onClick={handleDelete}
              disabled={isSubmitting || !isValid()}
              className="flex items-center space-x-2 px-5 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="delete-confirm-button"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Archiving...</span>
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  <span>Archive Part</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeletePartModal;
